<div class="rt-card rt-card-clickable">
  <div class="header">
    <div>
      <span class="title">{{ requisition.title }}</span>

      <rt-requisitions-status-badge [status]="requisition.status" [isArchived]="isArchived" />

      <rt-badge
        [text]="'#' + requisition.guid"
        color="secondary"
        [rtClickToCopy]="requisition.guid"
        [rtTooltip]="'app.offer.copy_id' | translate"
        class="guid-badge"
      />
    </div>
    <div layout>
      <rt-follow-button
        [type]="'requisition'"
        [followed]="requisition.followed"
        [loading]="false"
        (followClick)="follow.emit(requisition.id)"
        (unfollowClick)="unfollow.emit(requisition.id)"
      />
      @if (canEditRequisitions) {
        <rt-icon-button
          #moreDropdownTrigger="rtOverlayOrigin"
          variant="plain"
          (click)="moreDropdown.open = !moreDropdown.open"
          [pressed]="moreDropdown.open"
          rt-overlay-origin
          iconName="more-horiz"
          shape="square"
        />
        <ng-template
          [origin]="moreDropdownTrigger"
          (backdropClick)="moreDropdown.open = false"
          #moreDropdown="rtConnectedOverlay"
          rt-connected-overlay
          hasBackdrop
        >
          <rt-dropdown [width]="DropdownWidth.Small">
            @if (requisition.status === RequisitionStatus.Pending) {
              <rt-dropdown-item (click)="cancelRequest.emit(requisition.id)">
                <rt-icon prefix name="cancel" />
                {{ 'app.requisitions.actions.cancel_approval_request' | translate }}
              </rt-dropdown-item>
            }
            @if (!isArchived) {
              <rt-dropdown-item (click)="duplicate.emit(requisition.id)">
                <rt-icon prefix name="content-copy" />
                {{ 'app.offers.duplicate' | translate }}
              </rt-dropdown-item>
            }
            @if (!isArchived) {
              <rt-dropdown-item (click)="archive.emit(requisition)">
                <rt-icon prefix name="archive" />
                {{ 'app.offers.archive' | translate }}
              </rt-dropdown-item>
            }
            @if (isArchived) {
              <rt-dropdown-item (click)="retrieve.emit(requisition)">
                <rt-icon prefix name="archive" />
                {{ 'app.requisitions.actions.retrieve' | translate }}
              </rt-dropdown-item>
            }
            @if (!requisition.offersCount) {
              <rt-dropdown-item (click)="delete.emit(requisition)">
                <rt-icon prefix name="delete" />
                {{ 'app.common.delete' | translate }}
              </rt-dropdown-item>
            }
          </rt-dropdown>
        </ng-template>
      }
    </div>
  </div>
  <div class="body">
    <div class="hires-status-container">
      <div class="hires-status">
        <rt-heading class="counter" variant="heading large-x" [hasTextColorLight]="true">
          @if (requisition.openingsCount) {
            <span> {{ requisition.hiresCount }}/{{ requisition.openingsCount }} </span>
          }
          @if (!requisition.openingsCount) {
            <span>-</span>
          }
        </rt-heading>
        <div>{{ 'app.requisitions.list.hires' | translate }}</div>
      </div>
    </div>
    <div class="details-container" margin-left layout-full-width>
      <div class="details" layout layout-align-items-center margin-bottom-small-xx>
        <div
          class="details-item date-created"
          [rtTooltip]="'app.requisitions.list.date_created' | translate"
        >
          <span>{{ requisition.createdAt | amDateFormat: 'DD MMM YYYY' }}</span>
        </div>
        @if (requisition.department) {
          <rt-divider [variant]="'dot'" />
          <div
            #departmentRef
            [rtTooltip]="requisition.department.name"
            [rtTooltipEnabled]="departmentLabelTooltipEnabled"
            [class.restricted]="requisition.locations?.length"
            class="details-item department"
            layout-centered
          >
            <span>{{ requisition.department.name }}</span>
          </div>
        }
        @if (requisition.openingsCount && openingsCountText; as OpeningsCountInfo) {
          <rt-divider [variant]="'dot'" />
          <div class="details-item openings" layout-centered>
            {{ OpeningsCountInfo }}
          </div>
        }
        @if (requisition.locationIds?.length) {
          <rt-divider [variant]="'dot'" />
          <div class="details-item locations" layout-centered>
            <rt-assigned-locations-info-container
              [locationIds]="requisition.locationIds"
              [showIcon]="true"
            />
          </div>
        }
        @if (requisition.priority) {
          <rt-divider [variant]="'dot'" />
          <div layout-horizontal layout-align-items-center>
            <rt-icon
              [name]="priorityDataMap[requisition.priority].icon!"
              [colorToken]="priorityDataMap[requisition.priority].iconColorToken"
              margin-right-small-xx
            />
            <span>{{ priorityDataMap[requisition.priority].label }}</span>
          </div>
        }
      </div>
      @if (
        requisition.status === RequisitionStatus.Approved ||
        requisition.status === RequisitionStatus.Filled
      ) {
        <div layout layout-space-between>
          @if (requisition.offers.length) {
            <div class="jobs-details">
              <div class="jobs-list">
                @for (
                  offer of requisition.offers! | slice: 0 : 3;
                  track trackById($index, offer);
                  let first = $first
                ) {
                  <div
                    layout
                    layout-align-items-center
                    [routerLink]="
                      hasAccessToJob(offer.id) ? '/offers/' + offer.slug + '/pipeline' : null
                    "
                    class="job"
                    [class.not-clickable]="!hasAccessToJob(offer.id)"
                  >
                    <rt-offer-status-indicator
                      [offerStatus]="offer"
                      [attr.margin-left-small]="!first || null"
                      margin-right-small-x
                    />
                    <rt-truncate
                      class="title"
                      [style.max-width.px]="requisition.offers.length > 3 ? 180 : 220"
                    >
                      {{ offer.title }}
                    </rt-truncate>
                  </div>
                }
                @if (requisition.offers?.length! > 3) {
                  <rt-button
                    variant="plain"
                    [size]="'small'"
                    (click)="showJobsList.emit(requisition.offers)"
                    margin-left-small-x
                  >
                    {{ 'app.common.show_more' | translate }} ({{ requisition.offers.length - 3 }})
                  </rt-button>
                }
              </div>
            </div>
          } @else {
            {{ 'app.requisitions.list.no_job_assigned' | translate }}
          }
          @if (requisition.status === RequisitionStatus.Approved && canAddJobs && !isArchived) {
            <div>
              <rt-button [size]="'small'" (click)="addJob.emit(requisition.id)">
                <rt-icon name="add" />
                {{ 'app.requisitions.list.add_job' | translate }}
              </rt-button>
            </div>
          }
        </div>
      } @else {
        {{ 'app.requisitions.list.assigning_job_unavailable' | translate }}
      }
    </div>
    <a (click)="openRequisition.emit(requisition.id)" class="requisition-link"></a>
  </div>
</div>
